import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { MeProfile } from '../profile/me-profile.model';

export const MOVIDESK_CHAT_CLIENT_ID = 'B2DAC1D4D37B4C25A90563130128C306';
export const MOVIDESK_SUPPORT_PERMISSION_KEY = 'can_access_movidesk_chat';
const MOVIDESK_CONFIG_SCRIPT_ID = 'movidesk-chat-config';
const MOVIDESK_WIDGET_SCRIPT_ID = 'movidesk-chat-widget';
const MOVIDESK_WIDGET_SRC =
  'https://chat.movidesk.com/Scripts/chat-widget.min.js';

@Injectable({ providedIn: 'root' })
export class MovideskChatService {
  private readonly document = inject(DOCUMENT);
  private loaded = false;
  private widgetObserver?: MutationObserver;

  initialize(me: MeProfile | null | undefined): void {
    if (!me || this.loaded) return;
    if (!this.canTriggerSupport(me)) return;

    const body = this.document.body;
    if (!body) return;

    const configScript = this.document.createElement('script');
    configScript.id = MOVIDESK_CONFIG_SCRIPT_ID;
    configScript.type = 'text/javascript';
    configScript.text = `var mdChatClient="${MOVIDESK_CHAT_CLIENT_ID}";`;

    const widgetScript = this.document.createElement('script');
    widgetScript.id = MOVIDESK_WIDGET_SCRIPT_ID;
    widgetScript.src = MOVIDESK_WIDGET_SRC;
    widgetScript.async = true;
    widgetScript.defer = true;

    this.observeWidgetCreation();

    body.appendChild(configScript);
    body.appendChild(widgetScript);

    this.loaded = true;
  }

  destroy(): void {
    this.widgetObserver?.disconnect();
    this.widgetObserver = undefined;

    const ids = [MOVIDESK_CONFIG_SCRIPT_ID, MOVIDESK_WIDGET_SCRIPT_ID];

    for (const id of ids) {
      this.document.getElementById(id)?.remove();
    }

    const selectors = [
      '#md-app-widget',
      '[id^="md-app"]',
      'iframe[src*="movidesk"]',
      'iframe[id*="movidesk"]',
      'div[class*="movidesk"]',
    ];

    for (const selector of selectors) {
      this.document.querySelectorAll(selector).forEach((node) => node.remove());
    }

    const win = this.document.defaultView as any;

    if (win) {
      try {
        delete win.mdChatClient;
        delete win.mdChat;
        delete win.MovideskChat;
      } catch {
        win.mdChatClient = undefined;
      }
    }

    this.loaded = false;
  }

  private observeWidgetCreation(): void {
    this.widgetObserver?.disconnect();

    const configureWidget = (): boolean => {
      const widget = this.document.getElementById('md-app-widget');

      if (!widget) return false;

      widget.classList.remove('RightSide');
      widget.classList.add('LeftSide');

      return true;
    };

    if (configureWidget()) return;

    this.widgetObserver = new MutationObserver(() => {
      if (!configureWidget()) return;

      this.widgetObserver?.disconnect();
      this.widgetObserver = undefined;
    });

    this.widgetObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
    });
  }

  private canTriggerSupport(me: MeProfile): boolean {
    if (me.role === 'admin') return true;

    const keys =
      me.permissions?.map((permission) => permission.permission_key) ?? [];

    return keys.includes(MOVIDESK_SUPPORT_PERMISSION_KEY);
  }
}
